import { Link } from "react-router-dom";
import {
  Activity,
  Building2,
  CircleHelp,
  ExternalLink,
  KeyRound,
  MailPlus,
  User,
  Users,
} from "lucide-react";
import ResponsivePageHeader from "@/components/responsive/ResponsivePageHeader";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/LanguageContext";

const iconByName = {
  sensors: Activity,
  gyms: Building2,
  apiKeys: KeyRound,
  invites: MailPlus,
  members: Users,
  clients: User,
};

export default function Help() {
  const t = useTranslation();
  const quickStart = t("help.quickStart.items");
  const workflows = t("help.workflows");
  const broadcastDevices = t("help.broadcast.devices");
  const faq = t("help.faq.items");

  return (
    <div data-testid="help-page" className="space-y-6">
      <ResponsivePageHeader
        eyebrow={t("help.eyebrow")}
        title={t("help.title")}
        description={t("help.description")}
      />

      <section
        className="rounded-xl border border-border bg-card p-4 sm:p-5"
        data-testid="help-faq"
      >
        <SectionHeading
          eyebrow={t("help.faq.eyebrow")}
          title={t("help.faq.title")}
          description={t("help.faq.description")}
        />
        <div className="mt-4 rounded-xl border border-border bg-background p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
              <Activity className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display text-base tracking-tight">
                {t("help.intro.title")}
              </h3>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {t("help.intro.description")}
              </p>
            </div>
          </div>
        </div>
        <Accordion type="single" collapsible defaultValue="item-0" className="mt-2">
          {faq.map((item, index) => (
            <AccordionItem
              key={item.question}
              value={`item-${index}`}
              className="border-border"
            >
              <AccordionTrigger className="py-4 text-left text-sm hover:no-underline">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-xs leading-5 text-muted-foreground">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section
        className="rounded-xl border border-border bg-card p-4"
        data-testid="help-quick-start"
      >
        <SectionHeading
          eyebrow={t("help.quickStart.eyebrow")}
          title={t("help.quickStart.title")}
          description={t("help.quickStart.description")}
        />
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {quickStart.map((item, index) => (
            <div
              key={item.title}
              className="rounded-xl border border-border bg-background p-4"
            >
              <div className="mb-3 flex h-7 w-7 items-center justify-center rounded-lg border border-border font-mono text-[11px] text-muted-foreground">
                {index + 1}
              </div>
              <h3 className="font-display text-sm tracking-tight">{item.title}</h3>
              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3" data-testid="help-workflows">
        <SectionHeading
          eyebrow={t("help.workflowEyebrow")}
          title={t("help.workflowTitle")}
          description={t("help.workflowDescription")}
        />
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {workflows.map((workflow) => (
            <WorkflowGuide key={workflow.key} workflow={workflow} />
          ))}
        </div>
      </section>

      <section
        className="rounded-xl border border-border bg-card p-4"
        data-testid="help-heart-rate-broadcasting"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
            <Activity className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="mono-eyebrow mb-1">
              {t("help.broadcast.eyebrow")}
            </div>
            <h2 className="font-display text-lg tracking-tight">
              {t("help.broadcast.title")}
            </h2>
            <p className="mt-1 max-w-prose text-xs leading-5 text-muted-foreground">
              {t("help.broadcast.description")}
            </p>
            <div className="mt-3">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-8 rounded-lg text-xs"
              >
                <a
                  href="https://www.thisisant.com/directory"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("help.broadcast.checkCompatibility")}{" "}
                  <ExternalLink
                    className="ml-1 h-3.5 w-3.5"
                    strokeWidth={1.5}
                  />
                </a>
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
          {broadcastDevices.map((device) => (
            <article
              key={device.name}
              className="rounded-xl border border-border bg-background p-4"
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                <h3 className="font-display text-base tracking-tight">
                  {device.name}
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  {device.connection}
                </span>
              </div>
              <ol className="mt-3 space-y-2">
                {device.steps.map((step) => (
                  <li
                    key={step}
                    className="flex gap-3 text-xs leading-5 text-muted-foreground"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              {device.note && (
                <p className="mt-3 border-t border-border pt-3 text-xs leading-5 text-muted-foreground">
                  {device.note}
                </p>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function SectionHeading({ eyebrow, title, description }) {
  return (
    <div>
      <div className="mono-eyebrow mb-1">{eyebrow}</div>
      <h2 className="font-display text-lg tracking-tight">{title}</h2>
      {description && (
        <p className="mt-1 max-w-prose text-xs leading-5 text-muted-foreground">
          {description}
        </p>
      )}
    </div>
  );
}

function WorkflowGuide({ workflow }) {
  const Icon = iconByName[workflow.icon] || CircleHelp;

  return (
    <article
      className="flex flex-col rounded-xl border border-border bg-card p-4"
      data-testid={`help-workflow-${workflow.key}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <h3 className="font-display text-base tracking-tight">
            {workflow.title}
          </h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {workflow.description}
          </p>
        </div>
      </div>

      <ol className="mt-4 space-y-2">
        {workflow.steps.map((step) => (
          <li
            key={step}
            className="flex gap-3 text-xs leading-5 text-muted-foreground"
          >
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="mt-4 pt-2">
        <Button
          asChild
          variant="outline"
          size="sm"
          className="h-8 rounded-lg text-xs"
        >
          <Link to={workflow.to}>{workflow.action}</Link>
        </Button>
      </div>
    </article>
  );
}
